import {
  onClickOutside as _onClickOutside,
  type MaybeElementRef,
  type OnClickOutsideHandler,
  type OnClickOutsideOptions
} from '@vueuse/core'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { v4 as uuidv4 } from 'uuid'
import { getCurrentInstance, toHandlerKey } from 'vue'
import type { ISODateTimeString } from '@gat-ship/types'
import { Temporal, Intl } from '@js-temporal/polyfill'

/** @param obj Accepts a object of type T,
 * @returns a duplicate object with no reference to the object sent in to the param.  */
export function duplicateViaJson<T>(obj: T): T {
  if (obj) {
    const jsonStr = JSON.stringify(obj)
    return JSON.parse(jsonStr)
  }
  return obj
}

export function pascalToCamelCase(str: string): string {
  if (!str) return str
  return str.charAt(0).toLowerCase() + str.slice(1)
}

function catchError<T, U>(promsie: Promise<T>): Promise<[undefined, T] | [U]> {
  return promsie
    .then((data) => {
      return [undefined, data] as [undefined, T]
    })
    .catch((error) => {
      return [error] as [U]
    })
}

function onClickOutisde<T extends OnClickOutsideOptions>(
  target: MaybeElementRef,
  handler: OnClickOutsideHandler<T>,
  options?: T
) {
  return _onClickOutside<T>(target, handler, options)
}

function generateUuid() {
  return uuidv4()
}

// Function to generate a short unique ID starting with 'input-'
function generateShortUniqueId(prefix?: string) {
  const timestamp = Date.now().toString(36) // Convert timestamp to base-36 string
  const randomNum = Math.random().toString(36).substring(2, 8) // Generate a random base-36 string
  return `${prefix ? prefix + '-' : ''}${timestamp}-${randomNum}`
}

/**
 * Determines if the current component has a listener for a specific event
 *
 * See https://github.com/vuejs/core/blob/main/packages/runtime-core/src/componentEmits.ts#L166
 *
 * @param event Name of the event you wish to look for, eg: 'click'
 */
const camelizeRe = /-(\w)/g
function camelize(str: string): string {
  return str.replace(camelizeRe, (_, c: string) => (c ? c.toUpperCase() : ''))
}

const hyphenateRe = /\B([A-Z])/g
function hyphenate(str: string): string {
  return str.replace(hyphenateRe, '-$1').toLowerCase()
}

export function hasEmitListener(event: string) {
  const instance = getCurrentInstance()
  const props = instance?.vnode.props
  const isModelListener = event.startsWith('update:')
  if (!props) return false

  let handler =
    props[toHandlerKey(event)] ||
    // also try camelCase event handler (#2249)
    props[toHandlerKey(camelize(event))]

  if (!handler && isModelListener) {
    handler = props[toHandlerKey(hyphenate(event))]
  }

  return Boolean(handler)
}

function isIdenticalObjects(objA: unknown, objB: unknown): boolean {
  return JSON.stringify(objA) === JSON.stringify(objB)
}

function formatPhoneNumber(value: string | undefined | null) {
  let result = value
  if (result) {
    const parsed = parsePhoneNumber(result)
    if (parsed.valid) {
      return parsed.number
    }
  }
  if (!result) {
    result = undefined
  }
  return result
}

export type TimeRemainingResult<T extends boolean> = T extends true
  ? string
  : {
      years: number
      months: number
      days: number
      hours: number
      minutes: number
      seconds: number
    }

const TIME_REMAINING_UNITS = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds'
] as const

/** Overdue amounts only: a value that is zero or still in the future reads as 0. */
function overdueAmount(value: number | undefined): number {
  const floored = Math.floor(value ?? 0)
  return floored >= 0 ? 0 : Math.abs(floored)
}

function getTimeRemainingFromNow<T extends boolean = true>(
  date: Date | string,
  humanReadable: T = true as T,
  format?: T extends true
    ? {
        years?: string
        months?: string
        days?: string
        hours?: string
        minutes?: string
        seconds?: string
      }
    : never
): TimeRemainingResult<T> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const iso = typeof date === 'string' ? date : date.toISOString()
  const past = Temporal.Instant.from(iso).toZonedDateTimeISO(timeZone)
  const diff = now.since(past, { largestUnit: 'years' })

  if (!humanReadable) {
    return {
      years: overdueAmount(diff.years),
      months: overdueAmount(diff.months),
      days: overdueAmount(diff.days),
      hours: overdueAmount(diff.hours),
      minutes: overdueAmount(diff.minutes),
      seconds: overdueAmount(diff.seconds)
    } as TimeRemainingResult<T>
  }

  const parts = TIME_REMAINING_UNITS.map((unit) =>
    diff[unit]
      ? `${overdueAmount(diff[unit])} ${format?.[unit] ?? unit}`
      : undefined
  )
  const first = parts.findIndex((part) => part !== undefined)
  if (first === -1) {
    return 'Just now' as TimeRemainingResult<T>
  }
  // Gaps after the first present unit render as the string "undefined",
  // exactly as the original template literals did.
  return parts.slice(first).map(String).join(' ') as TimeRemainingResult<T>
}

function dateNow(
  hours?: number,
  minutes?: number,
  seconds?: number,
  timeZone?: string
): ISODateTimeString {
  // Temporal's `with()` takes an optional ZonedDateTimeLike; under
  // exactOptionalPropertyTypes, explicitly assigning `undefined` to
  // an optional field is rejected. Build the partial lazily.
  const withPartial: Temporal.ZonedDateTimeLike = {
    millisecond: 0,
    nanosecond: 0,
    microsecond: 0
  }
  if (hours !== undefined) withPartial.hour = hours
  if (minutes !== undefined) withPartial.minute = minutes
  if (seconds !== undefined) withPartial.second = seconds

  const now = Temporal.Now.zonedDateTimeISO(
    timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  ).with(withPartial)

  return now.toInstant().toString() as ISODateTimeString
}

function fromNow(options?: {
  days?: number
  months?: number
  years?: number
  hours?: number
  minutes?: number
  seconds?: number
  timeZone?: string
}) {
  const { days, months, years, hours, minutes, seconds, timeZone } =
    options ?? {}

  // Build `.with()` and `.add()` partials lazily — exactOptionalPropertyTypes
  // rejects explicit `undefined` on optional fields.
  const buildTimePartial = (): Temporal.ZonedDateTimeLike => {
    const p: Temporal.ZonedDateTimeLike = { millisecond: 0, nanosecond: 0 }
    if (hours !== undefined) p.hour = hours
    if (minutes !== undefined) p.minute = minutes
    if (seconds !== undefined) p.second = seconds
    return p
  }

  // If no duration parameters are provided, return current time.
  // Historically this function returned `Object.assign(utcString, { local })`
  // which silently boxed the primitive string into a `String` object and
  // tripped downstream `typeof value === 'string'` checks (zod v4 in
  // particular rejects with "expected string, received String"). No
  // consumer was using `.local`, so we return the plain UTC primitive.
  if (days === undefined && months === undefined && years === undefined) {
    const now = Temporal.Now.zonedDateTimeISO(
      timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    ).with(buildTimePartial())
    return now.toInstant().toString() as ISODateTimeString
  }

  const durationPartial: Temporal.DurationLike = {}
  if (days !== undefined) durationPartial.days = days
  if (months !== undefined) durationPartial.months = months
  if (years !== undefined) durationPartial.years = years

  const now = Temporal.Now.zonedDateTimeISO(
    timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  )
    .with(buildTimePartial())
    .add(durationPartial)

  return now.toInstant().toString() as ISODateTimeString
}

function dateForever() {
  return '2099-12-31T00:00:00Z' as ISODateTimeString
}

export function arrayToExcelFormat(
  data: Record<string, unknown>[],
  options: {
    headers: string[] // Make headers required
    delimiter?: string
    includeHeaders?: boolean
  }
): string {
  if (!data || data.length === 0) return ''

  const {
    headers,
    delimiter = '\t', // Tab delimiter for Excel compatibility
    includeHeaders = true
  } = options

  // Validate that headers are provided
  if (!headers || headers.length === 0) {
    throw new Error('Headers are required for data sanitization')
  }

  let csvContent = ''

  // Add headers if requested
  if (includeHeaders && headers.length > 0) {
    csvContent += headers.join(delimiter) + '\n'
  }

  // Add data rows
  for (const row of data) {
    const rowValues = headers.map((header) => {
      const value = row[header]
      if (value === null || value === undefined) return ''

      // Convert value to string and handle special cases
      let stringValue = String(value)

      // Escape quotes and wrap in quotes if contains delimiter, quotes, or newlines
      if (
        stringValue.includes(delimiter) ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        stringValue = `"${stringValue.replace(/"/g, '""')}"`
      }

      return stringValue
    })

    csvContent += rowValues.join(delimiter) + '\n'
  }

  return csvContent.trim()
}

export function copyArrayToExcelClipboard(
  data: Record<string, unknown>[],
  options: {
    headers: string[] // Make headers required
    delimiter?: string
    includeHeaders?: boolean
    showNotification?: boolean
  }
): Promise<boolean> {
  try {
    const excelContent = arrayToExcelFormat(data, options)

    if (!excelContent) {
      console.warn('No data to copy to clipboard')
      return Promise.resolve(false)
    }

    return navigator.clipboard.writeText(excelContent).then(() => {
      if (options?.showNotification !== false) {
        // You can integrate this with your notification system
        console.log('Data copied to clipboard in Excel format')
      }
      return true
    })
  } catch (error) {
    console.error('Failed to copy data to clipboard:', error)
    return Promise.resolve(false)
  }
}

/** A simple function to copy text to the clipboard. No formatting or validation. */
function copyToClipboard(text: string) {
  return navigator.clipboard.writeText(text)
}

export type ElementPosition = {
  left: number
  right: number
  top: number
  bottom: number
  absoluteLeft: number
  absoluteRight: number
  absoluteTop: number
  absoluteBottom: number
}

/**
 * Get the position of an element
 * @param el The element to get the position of
 * @returns The position of the element
 */
function getElementPosition(el: HTMLElement | null): ElementPosition {
  if (el) {
    const rect = el.getBoundingClientRect()
    return {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      absoluteLeft: rect.left + window.scrollX,
      absoluteRight: rect.right + window.scrollX,
      absoluteTop: rect.top + window.scrollY,
      absoluteBottom: rect.bottom + window.scrollY
    }
  }
  return {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    absoluteLeft: 0,
    absoluteRight: 0,
    absoluteTop: 0,
    absoluteBottom: 0
  }
}

export const globalHelperFunctions = {
  getTimeRemainingFromNow,
  duplicateViaJson,
  pascalToCamelCase,
  formatPhoneNumber,
  catchError,
  onClickOutisde,
  uuid: generateUuid,
  generateShortUniqueId,
  isIdenticalObjects,
  dateNow,
  dateForever,
  fromNow,
  arrayToExcelFormat,
  copyArrayToExcelClipboard,
  copyToClipboard,
  getElementPosition,
  components: {
    hasEmitListener
  }
}
